import { useEffect, useState } from 'react';
import { extractPatientAge } from '../../../helpers/patientAge';
import {
  BannerData,
  CampaignFilters,
  fetchActiveEmissionLoadingBanner,
  resolveDoctorSpeciality,
} from '../../../shared/bannerApi';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';

/**
 * Busca o criativo do slot patrocinado do loading de emissão.
 *
 * A busca acontece assim que a tela de emissão é montada, para o criativo já estar
 * disponível quando o loading abrir. Retorna `null` quando a flag está desligada,
 * quando não há campanha ativa ou em caso de erro.
 */
export function useEmissionLoadingBanner(): BannerData | null {
  const prescription = usePrescriptionStore((store) => store.prescription);
  const prescriberConfiguration = usePrescriptionStore((store) => store.prescriberConfiguration);
  const [banner, setBanner] = useState<BannerData | null>(null);

  const enableEmissionLoadingAds = !!prescriberConfiguration?.Features?.enableEmissionLoadingAds;
  const partnerId = prescriberConfiguration?.idParceiro;
  const prescriptionId = prescription?.idPrescricao;
  const prescriberId = prescription?.idPrescritor;
  const patientAge = prescription?.Paciente?.Idade;

  useEffect(() => {
    if (!enableEmissionLoadingAds || !partnerId) return;

    let isActive = true;

    const fetchBanner = async () => {
      const filters: CampaignFilters = {
        partner: [partnerId.toString()],
      };

      if (patientAge) {
        filters.age = extractPatientAge(patientAge) ?? '0';
      }

      // Lido no momento da busca: a referência dos RQEs muda a cada setPrescription e não deve refazer a busca.
      filters.speciality = await resolveDoctorSpeciality(
        usePrescriptionStore.getState().prescription?.Prescritor?.RQEs,
        prescriberId
      );

      if (prescriptionId && prescriberId) {
        filters.prescriptionParams = { prescriptionId, doctorId: prescriberId };
      }

      const activeBanner = await fetchActiveEmissionLoadingBanner(filters);
      if (isActive) setBanner(activeBanner);
    };

    fetchBanner();

    return () => {
      isActive = false;
    };
  }, [enableEmissionLoadingAds, partnerId, prescriptionId, prescriberId, patientAge]);

  return enableEmissionLoadingAds ? banner : null;
}
